// ============================================================================
// CONSTANTES
// ============================================================================

export const ITEMS_PER_PAGE = 10;

export const LIMIT_MESSAGE = "Limite de frequência mensal já alcançado para este ponto, dia e horário.";

export const STATUS_IGNORADOS_FREQUENCIA = ['Cancelado', 'Cancelado_Coordenador'];

const FREQUENCIA_PADRAO = 'Todas as semanas do mês';
const FREQUENCIA_CANONICA_POR_CHAVE = {
  'todas as semanas do mes': 'Todas as semanas do mês',
  'todas as semanas': 'Todas as semanas do mês',
  'primeira semana do mes': 'Primeira semana do mês',
  'primeira semana': 'Primeira semana do mês',
  'segunda semana do mes': 'Segunda semana do mês',
  'segunda semana': 'Segunda semana do mês',
  'terceira semana do mes': 'Terceira semana do mês',
  'terceira semana': 'Terceira semana do mês',
  'ultima semana do mes': 'Última semana do mês',
  'ultima semana': 'Última semana do mês',
  'duas primeiras semanas do mes': 'Duas primeiras semanas do mês',
  'duas primeiras semanas': 'Duas primeiras semanas do mês',
  'duas ultimas semanas do mes': 'Duas últimas semanas do mês',
  'duas ultimas semanas': 'Duas últimas semanas do mês',
};

// ID da região Torre de TV - usado para lógica especial de agrupamento
export const TORRE_DE_TV_REGION_ID = '685dfd5798e2136844fde981';

// Mapeamento de horários por período
export const HORARIOS_MANHA = ['6h - 8h', '8h - 10h', '10h - 12h'];
export const HORARIOS_TARDE = ['12h - 14h', '14h - 16h'];
export const HORARIOS_NOITE = ['18h - 20h'];

// ============================================================================
// FUNÇÕES DE DATA/SEMANA
// ============================================================================

/**
 * Retorna o número da semana do mês para uma data (1-5)
 */
export const getWeekOfMonth = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  return Math.floor((date.getDate() - 1) / 7) + 1;
};

/**
 * Retorna todas as ocorrências de um dia da semana específico em um mês
 */
export const getOccurrencesInMonth = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return [];
  }

  const year = date.getFullYear();
  const month = date.getMonth();
  const weekday = date.getDay();

  const occurrences = [];
  const firstOccurrence = new Date(year, month, 1);

  while (firstOccurrence.getDay() !== weekday) {
    firstOccurrence.setDate(firstOccurrence.getDate() + 1);
    if (firstOccurrence.getMonth() !== month) {
      return [];
    }
  }

  for (
    let current = new Date(firstOccurrence);
    current.getMonth() === month;
    current.setDate(current.getDate() + 7)
  ) {
    occurrences.push({ date: new Date(current), week: getWeekOfMonth(current) });
  }

  return occurrences;
};

/**
 * Cria um Set de semanas válidas
 */
export const createWeekSet = (values = []) => {
  return new Set(values.filter((week) => typeof week === 'number' && !Number.isNaN(week)));
};

export const normalizeFrequencyLabel = (frequency) => {
  if (typeof frequency !== 'string' || !frequency.trim()) {
    return FREQUENCIA_PADRAO;
  }

  const key = frequency
    .trim()
    .replace(/\s+/g, ' ')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

  const keyWithoutApenas = key.replace(/^apenas\s+(a\s+)?/, '');

  return (
    FREQUENCIA_CANONICA_POR_CHAVE[keyWithoutApenas]
    || FREQUENCIA_CANONICA_POR_CHAVE[key]
    || frequency.trim()
  );
};

/**
 * Retorna as semanas permitidas baseado na frequência configurada
 */
export const getAllowedWeeksForFrequency = (frequency = 'Todas as semanas do mês', occurrences = []) => {
  if (!Array.isArray(occurrences) || occurrences.length === 0) {
    return new Set();
  }

  const weeks = occurrences
    .map((occurrence) => occurrence.week)
    .filter((week) => typeof week === 'number');

  if (weeks.length === 0) {
    return new Set();
  }

  const frequencyMap = {
    'Primeira semana do mês': () => createWeekSet([weeks[0]]),
    'Segunda semana do mês': () => createWeekSet([weeks[1] ?? weeks[0]]),
    'Terceira semana do mês': () => createWeekSet([weeks[2] ?? weeks[weeks.length - 1]]),
    'Última semana do mês': () => createWeekSet([weeks[weeks.length - 1]]),
    'Duas primeiras semanas do mês': () => createWeekSet(weeks.slice(0, Math.min(2, weeks.length))),
    'Duas últimas semanas do mês': () => createWeekSet(weeks.slice(-Math.min(2, weeks.length))),
    'Todas as semanas do mês': () => createWeekSet(weeks),
  };

  const frequencyNormalized = normalizeFrequencyLabel(frequency);
  return (frequencyMap[frequencyNormalized] || frequencyMap[FREQUENCIA_PADRAO])();
};

// ============================================================================
// FUNÇÕES DE NORMALIZAÇÃO/FORMATAÇÃO
// ============================================================================

/**
 * Normaliza uma string removendo acentos e convertendo para minúsculas
 */
export const normalize = (s) => {
  return (s || '').toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
};

/**
 * Formata uma data ISO para o formato brasileiro (DD/MM/YYYY)
 */
export const formatDateBR = (dateStr) => {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.substring(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

// ============================================================================
// FUNÇÕES DE PONTO/REGIÃO
// ============================================================================

/**
 * Verifica se um ponto pertence à região Torre de TV
 * Aceita tanto o ponto selecionado quanto o ponto da disponibilidade
 */
export const isTorreDeTVRegion = (ponto) => {
  if (!ponto) return false;

  // Se for string, verifica se é o ID da região
  if (typeof ponto === 'string') {
    return ponto === TORRE_DE_TV_REGION_ID;
  }

  // Se for objeto, verifica múltiplos caminhos possíveis:
  const pontoId = ponto._id ? String(ponto._id) : null;
  const regiaoObj = ponto.regiao;
  
  // Verifica se o próprio ponto é a região (quando região é selecionada diretamente)
  if (pontoId === TORRE_DE_TV_REGION_ID) {
    return true;
  }

  // Se tem objeto região, verifica o _id
  if (regiaoObj) {
    // Caso 1: regiao é um objeto completo com _id (populate)
    if (typeof regiaoObj === 'object' && regiaoObj._id) {
      return String(regiaoObj._id) === TORRE_DE_TV_REGION_ID;
    }
    // Caso 2: regiao é string (ID direto sem populate)
    if (typeof regiaoObj === 'string') {
      return regiaoObj === TORRE_DE_TV_REGION_ID;
    }
  }

  return false;
};

/**
 * Verifica se um ponto corresponde à seleção do usuário
 * Para região Torre de TV, aceita qualquer ponto da região
 */
export const pontoMatchesSelection = (pontoField, pontoSelecionado) => {
  if (!pontoSelecionado) return true;
  if (!pontoField) return false;

  // Se o ponto selecionado pertence à região Torre de TV,
  // aceita qualquer ponto que também pertença a essa região
  if (isTorreDeTVRegion(pontoSelecionado)) {
    return isTorreDeTVRegion(pontoField);
  }

  // Modo normal: compara IDs
  const pontoId = pontoField?._id || pontoField;
  const selecionadoId = pontoSelecionado?._id || pontoSelecionado;
  return String(pontoId) === String(selecionadoId);
};

// ============================================================================
// FUNÇÕES DE PERÍODO
// ============================================================================

/**
 * Determina o período (Manhã/Tarde/Noite) baseado nos horários selecionados
 */
export const getPeriodoFromHorarios = (horarios = []) => {
  if (!Array.isArray(horarios) || horarios.length === 0) return null;

  const possuiManha = horarios.some((h) => HORARIOS_MANHA.includes(h));
  const possuiTarde = horarios.some((h) => HORARIOS_TARDE.includes(h));
  const possuiNoite = horarios.some((h) => HORARIOS_NOITE.includes(h));

  if (possuiManha) return 'Manhã';
  if (possuiTarde) return 'Tarde';
  if (possuiNoite) return 'Noite';
  return null;
};

// ============================================================================
// FUNÇÕES DE FILTRO/AGRUPAMENTO
// ============================================================================

/**
 * Remove disponibilidades duplicadas do array
 * Considera duplicada quando usuario, dia, periodo, frequencia e horarios são idênticos
 * O PONTO É IGNORADO NA COMPARAÇÃO para evitar duplicatas visuais quando exibindo múltiplos pontos (ex: Torre de TV)
 */
export const removerDisponibilidadesDuplicadas = (disponibilidades) => {
  // 1. Remove duplicatas exatas primeiro
  const map = new Map();
  
  disponibilidades.forEach(disp => {
    // Cria uma chave única baseada nos campos que definem uma disponibilidade
    // NOTA: Removemos o ponto da chave para unificar disponibilidades idênticas em pontos diferentes
    // Isso é essencial para a visualização da Torre de TV onde múltiplos pontos são exibidos juntos
    const horariosSorted = [...(disp.horario || [])].sort().join(',');
    const key = [
      disp.usuario?._id || disp.usuario,
      // disp.ponto?._id || disp.ponto, // Ponto removido da chave de unicidade
      disp.dia,
      disp.periodo,
      normalizeFrequencyLabel(disp.frequencia || FREQUENCIA_PADRAO),
      horariosSorted
    ].join('|');
    
    // Mantém apenas a primeira ocorrência
    if (!map.has(key)) {
      map.set(key, disp);
    }
  });
  
  const uniqueDisps = Array.from(map.values());

  // 2. Remove subconjuntos (ex: remover "8h-10h" se já existe "8h-10h / 10h-12h")
  const groups = {};
  
  uniqueDisps.forEach(disp => {
    const groupKey = [
      disp.usuario?._id || disp.usuario,
      disp.dia,
      disp.periodo,
      normalizeFrequencyLabel(disp.frequencia || FREQUENCIA_PADRAO)
    ].join('|');
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(disp);
  });

  const result = [];

  Object.values(groups).forEach(group => {
    // Se só tem 1 item no grupo, mantém
    if (group.length === 1) {
      result.push(group[0]);
      return;
    }

    // Filtra itens que são subconjuntos de outros
    const keptInGroup = group.filter((itemA) => {
      const hoursA = itemA.horario || [];
      
      // Verifica se itemA é subconjunto de algum itemB
      const isSubset = group.some((itemB) => {
        if (itemA === itemB) return false;
        const hoursB = itemB.horario || [];
        
        // Se B tem menos ou mesma quantidade de horários, A não pode ser subconjunto estrito
        // (duplicatas exatas já foram removidas no passo 1)
        if (hoursB.length <= hoursA.length) return false;

        // Verifica se todos os horários de A estão em B
        return hoursA.every(h => hoursB.includes(h));
      });

      return !isSubset;
    });
    
    result.push(...keptInGroup);
  });

  return result;
};

/**
 * Agrupa disponibilidades por ID do usuário
 */
export const agruparDisponibilidadesPorUsuario = (disponibilidades) => {
  // Remove duplicatas antes de agrupar
  const disponibilidadesUnicas = removerDisponibilidadesDuplicadas(disponibilidades);
  
  const agrupado = {};
  
  disponibilidadesUnicas.forEach((disp) => {
    const usuarioId = disp?.usuario?._id;
    if (!usuarioId) return;
    
    if (!agrupado[usuarioId]) {
      agrupado[usuarioId] = [];
    }
    agrupado[usuarioId].push(disp);
  });

  return agrupado;
};

/**
 * Filtra disponibilidades por dia e ponto
 */
export const filtrarDisponibilidades = (disponibilidades, { diaSelecionado, pontoSelecionado }) => {
  return disponibilidades.filter((disp) => {
    const usuarioId = disp.usuario?._id;
    if (!usuarioId) return false;

    // Filtro por dia
    if (diaSelecionado) {
      if (normalize(disp.dia) !== normalize(diaSelecionado)) return false;
    }

    // Filtro por ponto
    if (pontoSelecionado) {
      if (!pontoMatchesSelection(disp.ponto, pontoSelecionado)) return false;
    }

    return true;
  });
};

/**
 * Filtra disponibilidades por busca textual (nome ou congregação)
 */
export const filtrarPorBusca = (disponibilidades, searchTerm) => {
  if (!searchTerm?.trim()) return disponibilidades;

  const termo = searchTerm.toLowerCase();
  return disponibilidades.filter((disp) => {
    const nome = disp.usuario.nome?.toLowerCase() || "";
    const congregacao = disp.usuario.congregacao?.nome?.toLowerCase() || "";
    return nome.includes(termo) || congregacao.includes(termo);
  });
};

/**
 * Agrupa disponibilidades filtradas por publicador e ordena por nome
 */
export const agruparEOrdenarPublicadores = (disponibilidadesFiltradas) => {
  const agrupados = {};

  disponibilidadesFiltradas.forEach((disp) => {
    const usuarioId = disp.usuario?._id;
    if (!usuarioId) return;

    if (!agrupados[usuarioId]) {
      agrupados[usuarioId] = {
        usuario: disp.usuario,
        disponibilidades: [],
      };
    }
    agrupados[usuarioId].disponibilidades.push(disp);
  });

  return Object.values(agrupados).sort((a, b) => {
    const nomeA = (a.usuario?.nome || '').toLowerCase();
    const nomeB = (b.usuario?.nome || '').toLowerCase();
    return nomeA.localeCompare(nomeB, 'pt-BR');
  });
};

/**
 * Identifica publicadores que têm todos os horários selecionados
 */
export const getPublicadoresElegiveis = (dispsPorUsuario, horariosSelecionados) => {
  const elegiveis = new Set();

  Object.keys(dispsPorUsuario).forEach((usuarioId) => {
    const dispsUsuario = dispsPorUsuario[usuarioId];

    // Coleta todos os horários disponíveis do usuário
    const todosHorariosDisponiveis = new Set();
    dispsUsuario.forEach((disp) => {
      disp.horario.forEach((h) => todosHorariosDisponiveis.add(h));
    });

    // Verifica se o usuário tem TODOS os horários selecionados
    const temTodosOsHorarios =
      horariosSelecionados.length === 0 ||
      horariosSelecionados.every((h) => todosHorariosDisponiveis.has(h));

    if (temTodosOsHorarios) {
      elegiveis.add(usuarioId);
    }
  });

  return elegiveis;
};
